/* global runtime */
// Avstemmingsposteringer, valutaoppgjør og bilagskobling.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const nextBankReconciliationPostingId = (state, requestedId) => {
  const rawId = String(requestedId || '').trim();
  if (!rawId) return '';
  const baseId = rawId.replace(/-r\d+$/, '');
  const related = safeArray(state?.bankReconciliationPostings).filter((entry) => entry?.id === baseId || String(entry?.id || '').startsWith(`${baseId}-r`));
  const inactive = new Set(['annulled', 'reversed', 'deleted']);
  const active = related.find((entry) => !inactive.has(String(entry?.status || 'active').toLowerCase()));
  if (active) return String(active.id);
  if (!related.length) return baseId;
  const highestRevision = related.reduce((highest, entry) => {
    const match = String(entry?.id || '').match(/-r(\d+)$/);
    return Math.max(highest, match ? Number(match[1]) : 0);
  }, 0);
  return `${baseId}-r${highestRevision + 1}`;
};

const upsertBankReconciliationPosting = async (posting) => {
  const postingId = nextBankReconciliationPostingId(runtime.appState, posting.id || `bank-posting-${Date.now()}`);
  const postingLines = safeArray(posting.lines).map((line) => stripUndefinedDeep({
    accountCode: String(line?.accountCode || '').trim(),
    description: line?.description || posting.description || 'Bankpostering',
    signedAmount: num(line?.signedAmount),
  })).filter((line) => line.accountCode && num(line.signedAmount));
  const clean = stripUndefinedDeep({
    id: postingId,
    date: posting.date || todayIso(),
    description: posting.description || 'Bankpostering',
    amount: num(posting.amount),
    debitAccountCode: posting.debitAccountCode,
    creditAccountCode: posting.creditAccountCode,
    lines: postingLines.length ? postingLines : undefined,
    status: posting.status || 'matchet',
    voucherNumber: posting.voucherNumber || bankPostingVoucherNumber(postingId),
    bookingType: posting.bookingType,
    bankTrace: posting.bankTrace,
    createdAt: posting.createdAt || nowIso(),
    updatedAt: nowIso(),
  });
  try {
    const result = await bankReconciliationMutation('upsert_posting', { posting: clean });
    return result.posting || clean;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    await updateStateAndPersist((state) => ({
      ...state,
      bankReconciliationPostings: [
        clean,
        ...safeArray(state.bankReconciliationPostings).filter((entry) => entry.id !== clean.id),
      ],
    }));
    window.dispatchEvent(new Event(BACKEND_EVENTS.state));
    return clean;
  }
};
// Kursdifferanse gjores opp naar banklinjen avstemmes mot et valutabilag.
// Den faktiske NOK-belastningen fra banken er fasit; kursen bilaget ble bokfort
// med var et estimat fra Norges Bank.
const settleForeignCurrencyDifference = async ({ entryType, entryId, actualNok, date }) => {
  if (entryType !== 'expense') return undefined;
  const state = mergeState(runtime.appState);
  const expense = safeArray(state.expenses).find((entry) => entry.id === entryId);
  if (!expense || String(expense.currency || 'NOK').toUpperCase() === 'NOK') return undefined;

  const voucherDate = expense.date || date;
  const outcome = window.PlummeForeignCurrencyDifference.resolve({
    voucher: {
      currency: expense.currency,
      totalGross: expense.originalTotalGross,
      totalGrossNok: expense.totalGrossNok,
    },
    actualNok,
    locks: {
      vatFiled: Boolean(vatTermLockForDate(state, voucherDate)),
      yearClosed: Boolean(yearClosingLockForDate(state, voucherDate)),
    },
  });

  if (outcome.action === 'adjust') {
    // Skalerer linjene proporsjonalt: MVA-satser og kontofordeling er uendret,
    // bare kursen var feil.
    const currentGross = safeArray(expense.purchaseLines).reduce(
      (sum, line) => sum + num(line.amountNet) * (1 + num(line.vatRate) / 100),
      0,
    );
    if (!(currentGross > 0)) return undefined;
    const factor = outcome.amountNok / currentGross;
    const purchaseLines = safeArray(expense.purchaseLines).map((line) => ({
      ...line,
      amountNet: Math.round(num(line.amountNet) * factor * 100) / 100,
    }));
    await upsertPurchase({
      ...expense,
      purchaseLines,
      amountNet: purchaseLines.reduce((sum, line) => sum + num(line.amountNet), 0),
      totalGrossNok: outcome.amountNok,
      exchangeRate: outcome.exchangeRate,
      exchangeRateSource: outcome.exchangeRateSource,
      updatedAt: nowIso(),
    });
    return `Beløpet er justert til faktisk kurs fra banken (${expense.currency} ${num(expense.originalTotalGross)}).`;
  }

  if (outcome.action === 'post-difference') {
    const isLoss = outcome.accountCode === window.PlummeForeignCurrencyDifference.LOSS_ACCOUNT;
    await upsertBankReconciliationPosting({
      id: `fx-diff-${entryId}`,
      date: date || todayIso(),
      description: `Kursdifferanse ${expense.currency} - ${expense.description || 'kjøp'}`,
      amount: outcome.amount,
      // Tap debiteres 8160 mot leverandorgjeld/bank; gevinst krediteres 8060.
      debitAccountCode: isLoss ? outcome.accountCode : (expense.settlementAccountCode || '1920'),
      creditAccountCode: isLoss ? (expense.settlementAccountCode || '1920') : outcome.accountCode,
      bookingType: 'free-posting',
    });
    return `Perioden er låst. Kursdifferansen på ${outcome.amount.toFixed(2)} er ført på konto ${outcome.accountCode}.`;
  }

  return undefined;
};

const setBankLineReceiptRequirement = async (statementId, lineId, waived, reason = '') => {
  const result = await bankReconciliationMutation('set_receipt_requirement', { statementId, lineId, waived, reason });
  return result.statement ? mapBankStatementToUi(result.statement) : null;
};

const resolveBankReconciliationPosting = async (postingId, resolution) => {
  if (!postingId) throw new Error(t('svc.postedEntryNotFound'));
  const current = safeArray(runtime.appState?.bankReconciliationPostings).find((posting) => posting?.id === postingId);
  if (current?.bankMatchGroupId) {
    if (resolution?.resolution !== 'annul') throw new Error(t('bam.recon.internalTransfers'));
    const group = await reverseBankMatchGroup(current.bankMatchGroupId, resolution?.reason);
    return group;
  }
  const result = await bankReconciliationMutation('resolve_posting', {
    postingId,
    ...resolution,
  });
  return result.posting ? mapBankPostingToUi(result.posting) : null;
};

const correctBankLinkedPurchasePosting = async (expenseId, correction = {}) => {
  if (!expenseId) throw new Error(t('bam.recon.purchaseNotFound'));
  const result = await authorizedPost('purchaseMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'correct_bank_posting',
    expenseId,
    expense: correction,
  });
  if (result?.state) {
    runtime.appState = mergeAppMutationState(result.state);
    emitRuntime();
  }
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return result?.expense || null;
};

const attachReceiptToBankPosting = async (postingId, receiptId) => {
  if (!postingId) throw new Error(t('svc.postedEntryNotFound'));
  if (!receiptId) throw new Error(t('bam.recon.pickVoucher'));
  const result = await bankReconciliationMutation('attach_posting_receipt', { postingId, receiptId });
  return result.posting ? mapBankPostingToUi(result.posting) : null;
};

const detachReceiptFromBankLineLocal = (statementId, lineId) => {
  let detachedReceiptId = '';
  let detachedReceiptIds = [];
  updateState((state) => ({
    ...state,
    bankStatements: safeArray(state.bankStatements).map((statement) => statement.id === statementId ? {
      ...statement,
      lines: safeArray(statement.lines).map((line) => {
        if (line.id !== lineId) return line;
        const receiptIds = [
          ...safeArray(line.receiptAttachments).map((entry) => entry?.receiptInboxEntryId || entry?.id),
          line.receiptInboxEntryId || line.aiSuggestedReceiptInboxEntryId,
        ].map((id) => String(id || '').trim()).filter(Boolean);
        detachedReceiptId = receiptIds[0] || '';
        detachedReceiptIds = receiptIds;
        const nextLine = Object.assign({}, line);
        // Speiler backend: den kontinuerlige matchingen skal ikke koble det
        // samme paret tilbake igjen med en gang.
        if (receiptIds.length) nextLine.detachedReceiptInboxEntryIds = [...new Set([...safeArray(line.detachedReceiptInboxEntryIds).map((id) => String(id || '')), ...receiptIds])];
        delete nextLine.attachmentName;
        delete nextLine.attachmentId;
        delete nextLine.attachmentMimeType;
        delete nextLine.attachmentDownloadUrl;
        delete nextLine.receiptInboxEntryId;
        delete nextLine.receiptAttachments;
        delete nextLine.aiSuggestedReceiptInboxEntryId;
        delete nextLine.aiSuggestedReceiptMatchConfidence;
        delete nextLine.aiSuggestedReceiptMatchScore;
        delete nextLine.aiSuggestedReceiptMatchReason;
        return nextLine;
      }),
    } : statement),
    receiptInbox: safeArray(state.receiptInbox).map((entry) => {
      if (!detachedReceiptIds.includes(entry.id) || (entry.bookedAs && entry.bookedAs !== 'bank-line')) return entry;
      const unlinkedAnalysis = bankUnlinkedReceiptAnalysisPatch(entry, statementId, lineId);
      return {
        ...entry,
        status: bankReleasedReceiptStatus(entry),
        bookedAt: undefined,
        bookedAs: undefined,
        returnedFromBankLineAt: nowIso(),
        ...(unlinkedAnalysis ? { receiptAnalysis: unlinkedAnalysis } : {}),
      };
    }),
  }), true);
};

const detachReceiptFromBankLine = async (statementId, lineId) => {
  try {
    const result = await bankReconciliationMutation('detach_receipt', { statementId, lineId });
    return result.statement ? mapBankStatementToUi(result.statement) : null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    detachReceiptFromBankLineLocal(statementId, lineId);
    return null;
  }
};

const matchBankLineToBookedReceiptDuplicate = async (statementId, lineId) => {
  try {
    const result = await bankReconciliationMutation('match_booked_receipt_duplicate', { statementId, lineId });
    return result.statement ? mapBankStatementToUi(result.statement) : null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    let matched = null;
    await updateStateAndPersist((state) => {
      const statement = safeArray(state.bankStatements).find((entry) => entry.id === statementId);
      const line = safeArray(statement?.lines).find((entry) => entry.id === lineId);
      const receiptId = line?.receiptInboxEntryId || line?.aiSuggestedReceiptInboxEntryId;
      matched = getBookedReceiptDuplicateMatch(receiptId, state.receiptInbox);
      const entry = matched?.matchedEntryType === 'expense' ? safeArray(state.expenses).find((candidate) => candidate.id === matched?.matchedEntryId && candidate.status !== 'annulled') : safeArray(state.otherSales).find((candidate) => candidate.id === matched?.matchedEntryId && candidate.status !== 'annulled');
      const originalReceipt = safeArray(state.receiptInbox).find((candidate) => candidate.id === matched?.originalReceiptId);
      const directBookedReceipt = receiptId === matched?.originalReceiptId;
      const originalAmount = Math.abs(num(originalReceipt?.receiptAnalysis?.totalGross));
      if (!line || !matched) throw new Error(t('bam.recon.originalEntryNotFound'));
      if (!entry || ((num(line.amount) < 0) !== (matched.matchedEntryType === 'expense')) || !originalAmount || Math.abs(Math.abs(num(line.amount)) - originalAmount) >= .01) throw new Error(t('bam.recon.cannotLinkSafely'));
      const cleanLine = { ...line };
      ['attachmentName', 'attachmentId', 'attachmentMimeType', 'attachmentDownloadUrl', 'receiptInboxEntryId', 'aiSuggestedReceiptInboxEntryId', 'aiSuggestedReceiptMatchConfidence', 'aiSuggestedReceiptMatchScore', 'aiSuggestedReceiptMatchReason'].forEach((key) => { delete cleanLine[key]; });
      Object.assign(cleanLine, { bookingType: 'existing-entry', classification: 'business', reconciliationStatus: 'matched', matchedEntryType: matched.matchedEntryType, matchedEntryId: matched.matchedEntryId, resolvedDuplicateReceiptId: directBookedReceipt ? undefined : receiptId, resolvedBookedReceiptId: directBookedReceipt ? receiptId : undefined, note: `Avstemt mot allerede bokført post ${matched.matchedEntryId}.` });
      return { ...state, bankStatements: safeArray(state.bankStatements).map((entry) => entry.id === statementId ? { ...entry, lines: safeArray(entry.lines).map((candidate) => candidate.id === lineId ? cleanLine : candidate) } : entry), receiptInbox: directBookedReceipt ? safeArray(state.receiptInbox) : safeArray(state.receiptInbox).map((entry) => entry.id === receiptId ? { ...entry, status: 'trashed', bookedAt: undefined, bookedAs: undefined, linkedEntryId: undefined, duplicateOfReceiptId: matched.originalReceiptId, duplicateResolvedAt: nowIso() } : entry) };
    });
    return matched;
  }
};

const reopenBankExistingEntryMatch = async (statementId, lineId) => {
  try {
    const result = await bankReconciliationMutation('reopen_existing_entry_match', { statementId, lineId });
    return result.statement ? mapBankStatementToUi(result.statement) : null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    await updateStateAndPersist((state) => {
      const statement = safeArray(state.bankStatements).find((entry) => entry.id === statementId);
      const line = safeArray(statement?.lines).find((entry) => entry.id === lineId);
      if (!line || line.bookingType !== 'existing-entry' || !line.matchedEntryId) throw new Error(t('bam.recon.notLinked'));
      const originalIds = new Set(safeArray(state.receiptInbox)
        .filter((entry) => entry.linkedEntryId === line.matchedEntryId && entry.bookedAs === line.matchedEntryType)
        .map((entry) => entry.id));
      const directBookedReceiptId = line.resolvedBookedReceiptId;
      const receipt = safeArray(state.receiptInbox).find((entry) => entry.id === (line.resolvedDuplicateReceiptId || directBookedReceiptId))
        || safeArray(state.receiptInbox).find((entry) => ['trashed', 'trash'].includes(String(entry.status || '')) && originalIds.has(entry.duplicateOfReceiptId) && entry.duplicateResolvedAt);
      const attachment = safeArray(receipt?.attachments)[0];
      const reopened = { ...line };
      ['bookingType', 'classification', 'matchedEntryType', 'matchedEntryId', 'bankTrace', 'resolvedDuplicateReceiptId', 'resolvedBookedReceiptId'].forEach((key) => { delete reopened[key]; });
      Object.assign(reopened, {
        reconciliationStatus: 'manual-review',
        note: 'Koblingen til den eksisterende posten er åpnet igjen. Kontroller banklinjen på nytt.',
        receiptInboxEntryId: receipt?.id,
        attachmentName: receipt?.attachmentName,
        attachmentId: receipt?.attachmentId || attachment?.id,
        attachmentMimeType: receipt?.attachmentMimeType || attachment?.mimeType,
        attachmentDownloadUrl: attachment?.downloadUrl || attachment?.file || receipt?.downloadUrl || receipt?.file,
        bookedReceiptAutoMatchDismissedAt: directBookedReceiptId ? nowIso() : undefined,
      });
      return {
        ...state,
        bankStatements: safeArray(state.bankStatements).map((entry) => entry.id === statementId ? { ...entry, lines: safeArray(entry.lines).map((candidate) => candidate.id === lineId ? reopened : candidate) } : entry),
        receiptInbox: directBookedReceiptId ? safeArray(state.receiptInbox) : safeArray(state.receiptInbox).map((entry) => entry.id === receipt?.id ? { ...entry, status: 'active', duplicateResolvedAt: undefined, reopenedFromBankMatchAt: nowIso() } : entry),
      };
    });
    return null;
  }
};

const reopenInvestmentBankMatch = async (statementId, lineId) => {
  const result = await bankReconciliationMutation('reopen_investment_bank_match', { statementId, lineId });
  return result.statement ? mapBankStatementToUi(result.statement) : null;
};

const buildLoanDebtPostingLines = ({ amount, accountCode, bankAccountCode, description, principalAmount = 0, interestAmount = 0, feeAmount = 0 }) => {
  const total = Math.abs(num(amount));
  const principal = Math.max(0, num(principalAmount));
  const interest = Math.max(0, num(interestAmount));
  const fee = Math.max(0, num(feeAmount));
  const hasSplit = principal + interest + fee > 0;
  if (!hasSplit) return [];
  const expectedBankAmount = num(amount) < 0 ? principal + interest + fee : principal - interest - fee;
  if (Math.abs(total - expectedBankAmount) > 0.005) throw new Error(t('bam.recon.loanSumMismatch'));
  const lines = [];
  if (num(amount) < 0) {
    if (principal) lines.push({ accountCode, description: `${description} - avdrag`, signedAmount: principal });
    if (interest) lines.push({ accountCode: '8150', description: `${description} - renter`, signedAmount: interest });
    if (fee) lines.push({ accountCode: '7770', description: `${description} - gebyr`, signedAmount: fee });
    lines.push({ accountCode: bankAccountCode, description, signedAmount: -total });
  } else {
    lines.push({ accountCode: bankAccountCode, description, signedAmount: total });
    if (interest) lines.push({ accountCode: '8150', description: `${description} - renter`, signedAmount: interest });
    if (fee) lines.push({ accountCode: '7770', description: `${description} - gebyr`, signedAmount: fee });
    if (principal) lines.push({ accountCode, description: `${description} - lånebeløp`, signedAmount: -principal });
  }
  const balance = lines.reduce((sum, line) => sum + num(line.signedAmount), 0);
    if (Math.abs(balance) >= 0.01) throw new Error(t('bam.recon.loanSplitUnbalanced'));
  return lines;
};

const buildControlledBankLinePosting = ({ bookingType, statementId, lineId, date, description, amount, accountCode, bankAccountCode, bankTrace, loanPrincipalAmount = 0, loanInterestAmount = 0, loanFeeAmount = 0 }) => {
  const absoluteAmount = Math.abs(num(amount));
  if (!['rounding', 'bank-fee', 'loan-debt', 'owner-settlement'].includes(bookingType)) throw new Error(t('svc.invalidBankEntry'));
  if (bookingType === 'rounding' && absoluteAmount >= 1) {
    throw new Error(t('svc.yearDiffTooLarge'));
  }
  const expenseAccountCode = String(accountCode || '').trim();
  if (!expenseAccountCode) throw new Error(t('svc.pickBankAccount'));
  const loanLines = bookingType === 'loan-debt'
    ? buildLoanDebtPostingLines({ amount, accountCode: expenseAccountCode, bankAccountCode, description, principalAmount: loanPrincipalAmount, interestAmount: loanInterestAmount, feeAmount: loanFeeAmount })
    : [];
  return {
    id: `bank-posting-${bookingType}-${statementId}-${lineId}`,
    date,
    description,
    amount: num(amount),
    debitAccountCode: num(amount) < 0 ? expenseAccountCode : bankAccountCode,
    creditAccountCode: num(amount) < 0 ? bankAccountCode : expenseAccountCode,
    lines: loanLines.length ? loanLines : undefined,
    bookingType,
    bankTrace,
  };
};
